import React from 'react';
import { AbsoluteFill, Img, Sequence, interpolate, useCurrentFrame } from 'remotion';
import type { AfbeeldingItem, ProjectModel } from '@video-studio/shared';
import { passingNaarObjectFit, OntbrekendeMedia } from './VideoLaag.js';

/**
 * Afbeeldingslaag. Geschaald en gepositioneerd binnen het kader met behoud van
 * verhoudingen, of precies passend bij de gekozen passing.
 *
 * `beweging` zoomt langzaam in of uit over de lengte van de clip, zodat een
 * stilstaande productfoto niet als een dia aanvoelt.
 */
export interface AfbeeldingLaagProps {
  item: AfbeeldingItem;
  project: ProjectModel;
  assetUrls: Record<string, string>;
  assetAfmetingen?: Record<string, { breedte: number; hoogte: number }>;
}

export const AfbeeldingLaag: React.FC<AfbeeldingLaagProps> = ({ item, assetUrls }) => {
  const url = assetUrls[item.assetId];
  return (
    <Sequence
      from={Math.max(0, Math.round(item.startFrame))}
      durationInFrames={Math.max(1, Math.round(item.duurInFrames))}
      name={item.naam || 'Afbeelding'}
    >
      <AbsoluteFill>
        {url ? (
          <BewegendBeeld item={item} url={url} />
        ) : (
          <OntbrekendeMedia tekst={`Afbeelding ontbreekt: ${item.naam || item.assetId}`} />
        )}
      </AbsoluteFill>
    </Sequence>
  );
};

const BewegendBeeld: React.FC<{ item: AfbeeldingItem; url: string }> = ({ item, url }) => {
  const frame = useCurrentFrame();
  const einde = Math.max(1, item.duurInFrames - 1);
  const basis = item.schaal;
  const ver = basis * 1.12;
  const schaal =
    item.beweging === 'inzoomen'
      ? interpolate(frame, [0, einde], [basis, ver], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
      : item.beweging === 'uitzoomen'
        ? interpolate(frame, [0, einde], [ver, basis], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
        : basis;

  return (
    <Img
      src={url}
      style={{
        width: '100%',
        height: '100%',
        objectFit: passingNaarObjectFit(item.passing),
        transform: `translate(${(item.x - 0.5) * 100}%, ${(item.y - 0.5) * 100}%) scale(${schaal}) rotate(${item.rotatie}deg)`,
        opacity: item.dekking,
      }}
    />
  );
};
